import {pickKeysLike} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {SVGProps} from 'react';
import {Icon as SvgIcon} from 'react-lazy-svg';
import {COLORS} from '../../../constants/colors';
import type {IconName} from './IconName';
import {useSharedIconUrl} from './IconName';

export enum IconSize {
  XXS = 8,
  XSS = 12,
  XS = 16,
  SS = 20,
  S = 24,
  M = 32,
  L = 48,
  XL = 54,
  XXL = 90,
}

type AllowedSvgProps = Pick<
  SVGProps<SVGSVGElement>,
  | 'className'
  | 'fill'
  | 'fillOpacity'
  | 'strokeWidth'
  | 'color'
  | 'ref'
  | 'onClick'
  | 'height'
  | 'width'
  | 'cursor'
>;

interface IconProps extends AllowedSvgProps {
  asset: IconName;
  size?: keyof typeof IconSize;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
  [dataAttr: `data-${string}`]: string;
}

type SVGIconType = {url: string} & AllowedSvgProps &
  Pick<
    SVGProps<SVGSVGElement>,
    'aria-hidden' | 'aria-label' | 'role' | 'focusable'
  >;
const SvgIconWithFill = styled(SvgIcon)<{fill: string} & SVGIconType>`
  display: block;
  flex-grow: 0;
  flex-shrink: 0;
  fill: ${({fill}) => fill};
`;

const SvgIconWithSize = styled(SvgIconWithFill)<{size: string}>`
  width: ${({size}) => size};
  height: ${({size}) => size};
  flex-basis: ${({size}) => size};
`;

export const Icon = ({
  asset,
  className,
  size,
  fill = COLORS.Black,
  fillOpacity,
  strokeWidth,
  style,
  color,
  onClick,
  height,
  width,
  cursor,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  ref,
  ...rest
}: IconProps) => {
  const dataAttrProps = pickKeysLike(rest, 'data-');
  const a11yProps = ariaLabel
    ? {role: 'img', 'aria-label': ariaLabel}
    : {'aria-hidden': true, focusable: false};

  const url = useSharedIconUrl(asset);
  if (!url) {
    return null;
  }

  if (!size) {
    return (
      <SvgIconWithFill
        data-testid={dataTestId}
        className={className}
        fill={fill}
        fillOpacity={fillOpacity}
        strokeWidth={strokeWidth}
        color={color}
        style={style}
        onClick={onClick}
        height={height}
        width={width}
        cursor={cursor}
        url={url}
        ref={ref}
        {...a11yProps}
        {...dataAttrProps}
      />
    );
  }

  return (
    <SvgIconWithSize
      data-testid={dataTestId}
      className={className}
      fill={fill}
      size={`${IconSize[size]}px`}
      fillOpacity={fillOpacity}
      strokeWidth={strokeWidth}
      color={color}
      style={style}
      onClick={onClick}
      height={height}
      width={width}
      cursor={cursor}
      url={url}
      ref={ref}
      {...a11yProps}
      {...dataAttrProps}
    />
  );
};
